import { MAX_TURNS, wholeLessonIds } from '@platform/schema/chat';

import type {
  AppliedProposals,
  ChatTurn,
  PlanningProposal,
} from '@platform/schema/chat';

/** What became of one proposal. Absent while it is still the admin's to decide. */
export type Outcome =
  | { state: 'applied' }
  | { state: 'dismissed' }
  | {
      state: 'refused';
      reason: AppliedProposals['refused'][number]['reason'];
      message: string;
    };

/**
 * The server keeps no conversation, so each question re-sends the history. An even
 * number of past turns keeps the first one the admin's.
 */
export const turnsFor = (
  history: readonly ChatTurn[],
  question: string,
): ChatTurn[] => [
  ...history.slice(-(MAX_TURNS - 2)),
  { role: 'operator', text: question },
];

export interface Segment {
  text: string;
  lessonId?: string;
}

/** The answer's text, with each Lesson ID it relies on cut out to become a link. */
export function linked(text: string, lessonIds: readonly string[]): Segment[] {
  if (lessonIds.length === 0) return [{ text }];
  const ids = new Set(lessonIds);
  return text
    .split(new RegExp(wholeLessonIds(lessonIds), 'g'))
    .filter((part) => part !== '')
    .map((part) =>
      ids.has(part) ? { text: part, lessonId: part } : { text: part },
    );
}

/** Which of the asked proposals landed, by position in the answer. */
export function outcomes(
  asked: readonly number[],
  proposals: readonly PlanningProposal[],
  result: {
    applied: readonly { lessonId: string }[];
    refused: AppliedProposals['refused'];
  },
): Record<number, Outcome> {
  const applied = new Set(result.applied.map((row) => row.lessonId));
  const settled: Record<number, Outcome> = {};
  for (const at of asked) {
    const lessonId = proposals[at]?.lessonId;
    const refused = result.refused.find((one) => one.lessonId === lessonId);
    if (refused !== undefined)
      settled[at] = {
        state: 'refused',
        reason: refused.reason,
        message: refused.message,
      };
    else if (lessonId !== undefined && applied.has(lessonId))
      settled[at] = { state: 'applied' };
  }
  return settled;
}
